import { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { ThemeProvider, createTheme, useTheme } from '@mui/material/styles'
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded'
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded'
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import { useNavigate, useParams } from 'react-router-dom'
import EventBrandShell from '../components/EventBrandShell'
import { LoadingState } from '../components/ui/States'
import { buildEventPalette } from '../theme/eventTheme'
import { eventService } from '../services/eventService'
import { attendeeAuth } from '../services/attendeeAuth'
import { useAttendeeAuth } from '../contexts/AttendeeAuthContext'
import { formatDateRange } from '../utils/format'
import type { EventInfo } from '../data/types'

/**
 * Per-event branded sign-in page (/e/:slug/signin). Renders the event's own
 * logo, colors, and login background, then a magic-link sign-in form. This is
 * the branded default entry for a per-event link; the generic "/" directory
 * remains as a fallback.
 */
export default function EventSignInPage() {
  const { slug = '' } = useParams()
  const navigate = useNavigate()
  const { isAuthenticated, me } = useAttendeeAuth()
  const [event, setEvent] = useState<EventInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)

  // Already signed in → go into the event.
  useEffect(() => {
    if (isAuthenticated) {
      navigate(me?.eventSlug ? `/e/${me.eventSlug}` : `/e/${slug}`, { replace: true })
    }
  }, [isAuthenticated, me, navigate, slug])

  useEffect(() => {
    eventService
      .getEventBySlug(slug)
      .then(setEvent)
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false))
  }, [slug])

  if (loading) return <LoadingState />

  if (notFound || !event) {
    return (
      <EventBrandShell brand={{}}>
        <Typography variant="h6" fontWeight={700} gutterBottom>
          Event not found
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          This sign-in link doesn't match a published event. Please check the
          link, or contact your Vericast event manager.
        </Typography>
        <Button variant="outlined" onClick={() => navigate('/')}>
          All events
        </Button>
      </EventBrandShell>
    )
  }

  return (
    <EventThemed primaryColor={event.primaryColor} accentColor={event.accentColor}>
      <EventBrandShell
        brand={{
          name: event.name,
          tagline: event.tagline,
          primaryColor: event.primaryColor,
          accentColor: event.accentColor,
          logoUrl: event.logoUrl,
          loginBackgroundUrl: event.loginBackgroundUrl,
          heroImage: event.heroImage,
        }}
      >
        <SignInForm event={event} />
      </EventBrandShell>
    </EventThemed>
  )
}

/** Applies the event's brand palette over the base theme (buttons/inputs). */
function EventThemed({
  primaryColor,
  accentColor,
  children,
}: {
  primaryColor?: string | null
  accentColor?: string | null
  children: React.ReactNode
}) {
  const base = useTheme()
  const themed = useMemo(() => {
    const override = buildEventPalette(primaryColor, accentColor)
    if (!override) return base
    return createTheme(base, {
      palette: {
        primary: { ...base.palette.primary, ...override.primary },
        ...(override.secondary
          ? { secondary: { ...base.palette.secondary, ...override.secondary } }
          : {}),
      },
    })
  }, [base, primaryColor, accentColor])
  return <ThemeProvider theme={themed}>{children}</ThemeProvider>
}

function SignInForm({ event }: { event: EventInfo }) {
  const [email, setEmail] = useState('')
  const [sent, setSent] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const handleContinue = async () => {
    if (!email.trim()) return
    setSubmitting(true)
    try {
      await attendeeAuth.requestLink(email.trim(), event.slug ?? '')
      setSent(true)
    } catch {
      setSent(true) // neutral "check your inbox" (no enumeration leak)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h5" fontWeight={700}>
          {event.name}
        </Typography>
        <Chip label="Sign in" size="small" />
      </Stack>
      <Stack spacing={1.5} sx={{ mb: 3 }}>
        {event.startsAt && event.endsAt && (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <CalendarMonthRoundedIcon fontSize="small" color="action" />
            <Typography variant="body2" fontWeight={600}>
              {formatDateRange(event.startsAt, event.endsAt)}
            </Typography>
          </Stack>
        )}
        {event.location && (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <PlaceRoundedIcon fontSize="small" color="action" />
            <Typography variant="body2" fontWeight={600}>
              {event.location}
            </Typography>
          </Stack>
        )}
      </Stack>

      {sent ? (
        <Box
          sx={{
            p: 3,
            borderRadius: 3,
            bgcolor: 'rgba(0,117,188,0.06)',
            border: '1px solid',
            borderColor: 'divider',
            textAlign: 'center',
          }}
        >
          <Typography fontWeight={700} gutterBottom>
            Check your inbox
          </Typography>
          <Typography variant="body2" color="text.secondary">
            If {email} is on the guest list for {event.name}, we've sent a secure
            sign-in link. Open it on this device to continue.
          </Typography>
        </Box>
      ) : (
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Enter the email on your invitation and we'll send you a secure
            sign-in link.
          </Typography>
          <TextField
            label="Email address"
            type="email"
            fullWidth
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleContinue()}
            autoComplete="email"
          />
          <Button
            variant="contained"
            size="large"
            fullWidth
            endIcon={<ArrowForwardRoundedIcon />}
            onClick={handleContinue}
            disabled={submitting || !email.trim()}
          >
            {submitting ? 'Sending…' : 'Email me a link'}
          </Button>
          <Divider sx={{ my: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Invited guests only
            </Typography>
          </Divider>
          <Typography variant="caption" color="text.secondary" textAlign="center">
            Having trouble? Contact your Vericast event manager.
          </Typography>
        </Stack>
      )}
    </>
  )
}
